import { useCallback, useSyncExternalStore } from 'react';

import type { Surface } from './surfaces';

/**
 * RAIL ORDER — a rendering preference, exactly like the palette.
 *
 * The design of record makes the rail's sections draggable. What that reorders is the ORDER
 * of the entries a person may already enter — never the membership. `apply()` below can only
 * permute the list it is handed, so a stored order can never introduce an entry, resurrect a
 * retired surface, or survive a capability change: `navSurfaces()` filters first, and this
 * sorts what is left. THE ONE DECLARATION still decides who sees what.
 *
 * It persists client-side only. It is not a fact about the account, so it never goes near
 * the server, and a browser that refuses storage simply gets the declared order.
 */

const STORAGE_KEY = 'accounts.navOrder';

function readStored(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === 'string');
  } catch {
    // Storage refused, or the value was not JSON: fall back to the declared order.
    return [];
  }
}

let snapshot: readonly string[] = readStored();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): readonly string[] {
  return snapshot;
}

function emit(next: readonly string[]): void {
  snapshot = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // An order that cannot be stored still applies for this page's lifetime.
  }
  for (const listener of listeners) listener();
}

/** Sort `entries` by the stored order; anything unknown to it keeps its declared position. */
function apply(entries: readonly Surface[], order: readonly string[]): readonly Surface[] {
  if (order.length === 0) return entries;
  const rank = new Map(order.map((id, index) => [id, index]));
  return [...entries].sort((a, b) => {
    const left = rank.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const right = rank.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    if (left !== right) return left - right;
    return entries.indexOf(a) - entries.indexOf(b);
  });
}

export interface NavOrder {
  readonly entries: readonly Surface[];
  /** Move `movedId` to where `targetId` currently sits. */
  readonly move: (movedId: string, targetId: string) => void;
}

export function useNavOrder(entries: readonly Surface[]): NavOrder {
  const order = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const ordered = apply(entries, order);

  const move = useCallback(
    (movedId: string, targetId: string) => {
      if (movedId === targetId) return;
      const ids = ordered.map((entry) => entry.id);
      const without = ids.filter((id) => id !== movedId);
      const at = without.indexOf(targetId);
      without.splice(at < 0 ? without.length : at, 0, movedId);
      emit(without);
    },
    [ordered],
  );

  return { entries: ordered, move };
}
